/**
 * State Variables Utility
 * Computes available state variables for a node based on graph topology
 * Uses backend-parsed schemas for 100% accuracy
 */

/**
 * Fallback schema when graph has no stateSchema defined
 * Used for backward compatibility
 */
const FALLBACK_CONTEXT_SCHEMA = {
  ticketContext: {
    type: 'object',
    label: 'Ticket Context',
    description: 'Jira ticket information',
    properties: {
      ticketKey: { type: 'string', label: 'Ticket Key', example: 'TC-3' },
      summary: { type: 'string', label: 'Summary', example: '[Backend] Feature implementation' },
      description: { type: 'string', label: 'Description', example: 'Full ticket description text' },
    }
  },
  workspace: { 
    type: 'string', 
    label: 'Workspace',
    description: 'Workspace directory path',
    example: '/workspace' 
  },
  model: { 
    type: 'string', 
    label: 'AI Model',
    description: 'Selected AI model',
    example: 'auto' 
  }
};

/**
 * Convert framework state schema to frontend display format
 */
function convertStateSchemaToDisplay(schema) {
  if (!schema) return FALLBACK_CONTEXT_SCHEMA;
  
  // Handle JSON Schema format (from zodToJsonSchema)
  // JSON Schema has { type: 'object', properties: { ... }, required: [...] }
  const properties = schema.properties || schema;
  const requiredFields = schema.required || [];
  
  const result = {};
  for (const [key, fieldDef] of Object.entries(properties)) {
    // Skip JSON Schema meta fields
    if (['type', 'required', 'additionalProperties', '$schema'].includes(key)) continue;
    
    const isRequired = requiredFields.includes(key);
    
    result[key] = {
      type: fieldDef.type || 'unknown',
      label: fieldDef.description || formatLabel(key),
      description: fieldDef.description,
      optional: !isRequired,
    };
    
    // Handle nested objects (ticketContext, repos, etc.)
    if (fieldDef.type === 'object' && fieldDef.properties) {
      const nestedRequired = fieldDef.required || [];
      result[key].properties = {};
      for (const [propKey, propDef] of Object.entries(fieldDef.properties)) {
        result[key].properties[propKey] = {
          type: propDef.type || 'unknown',
          label: propDef.description || formatLabel(propKey),
          description: propDef.description,
          optional: !nestedRequired.includes(propKey),
        };
        
        // Handle deeply nested objects
        if (propDef.type === 'object' && propDef.properties) {
          result[key].properties[propKey].properties = {};
          for (const [deepKey, deepDef] of Object.entries(propDef.properties)) {
            result[key].properties[propKey].properties[deepKey] = {
              type: deepDef.type || 'unknown',
              label: deepDef.description || formatLabel(deepKey),
              description: deepDef.description,
            };
          }
        }
      }
    }
    
    // Handle arrays with item schemas
    if (fieldDef.type === 'array' && fieldDef.items) {
      result[key].itemType = fieldDef.items.type;
      if (fieldDef.items.properties) {
        result[key].itemProperties = fieldDef.items.properties;
      }
    }
  }
  return result;
}

/**
 * Get all predecessor nodes in topological (execution) order
 * @param {string} nodeId - Target node ID
 * @param {Array} nodes - All nodes in the graph
 * @param {Array} edges - All edges in the graph
 * @returns {Array} Array of predecessor node objects in execution order
 */
export function getPredecessorNodesInOrder(nodeId, nodes, edges) {
  // Build adjacency list (forward direction)
  const successors = new Map();
  const predecessorMap = new Map();
  
  for (const node of nodes) {
    successors.set(node.id, []);
    predecessorMap.set(node.id, []);
  }
  
  for (const edge of edges) {
    if (successors.has(edge.source)) {
      successors.get(edge.source).push(edge.target);
    }
    if (predecessorMap.has(edge.target)) {
      predecessorMap.get(edge.target).push(edge.source);
    }
  }
  
  // Find all predecessors using BFS backward from nodeId
  const predecessorIds = new Set();
  const queue = [nodeId];
  const visited = new Set([nodeId]);
  
  while (queue.length > 0) {
    const current = queue.shift();
    const preds = predecessorMap.get(current) || [];
    
    for (const pred of preds) {
      if (!visited.has(pred)) {
        visited.add(pred);
        predecessorIds.add(pred);
        queue.push(pred);
      }
    }
  }
  
  // Topological sort using Kahn's algorithm (only for predecessors)
  const relevantNodes = nodes.filter(n => predecessorIds.has(n.id));
  const inDegree = new Map();
  
  for (const node of relevantNodes) {
    inDegree.set(node.id, 0);
  }
  
  for (const edge of edges) {
    if (predecessorIds.has(edge.source) && predecessorIds.has(edge.target)) {
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    }
  }
  
  // Start with nodes that have no incoming edges (within our subgraph)
  const sortQueue = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) {
      sortQueue.push(id);
    }
  }
  
  const sorted = [];
  while (sortQueue.length > 0) {
    const current = sortQueue.shift();
    sorted.push(current);
    
    const succs = successors.get(current) || [];
    for (const succ of succs) {
      if (inDegree.has(succ)) {
        const newDegree = inDegree.get(succ) - 1;
        inDegree.set(succ, newDegree);
        if (newDegree === 0) {
          sortQueue.push(succ);
        }
      }
    }
  }
  
  // Map sorted IDs to node objects
  return sorted.map(id => nodes.find(n => n.id === id)).filter(Boolean);
}

/**
 * Parse executeCode to extract return statement properties
 * Simple regex-based extraction - handles common patterns
 * @param {string} executeCode - The execute function code
 * @returns {Array} Array of variable definitions extracted from return statements
 */
function parseExecuteCodeReturns(executeCode) {
  if (!executeCode || typeof executeCode !== 'string') return [];
  
  const variables = [];
  const foundPaths = new Set();
  
  // Match return { ... } patterns (handles multi-line)
  const returnRegex = /return\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/gs;
  let match;
  
  while ((match = returnRegex.exec(executeCode)) !== null) {
    const returnContent = match[1];
    
    // Extract property names (key: value or key,)
    const propRegex = /(\w+)\s*[,:]/g;
    let propMatch;
    
    while ((propMatch = propRegex.exec(returnContent)) !== null) {
      const propName = propMatch[1];
      // Skip common keywords and already found
      if (['type', 'const', 'let', 'var', 'if', 'else', 'return', 'true', 'false'].includes(propName)) continue;
      if (foundPaths.has(propName)) continue;
      
      foundPaths.add(propName);
      
      // Try to infer type from the value
      const valuePattern = new RegExp(`${propName}\\s*:\\s*([^,}]+)`);
      const valueMatch = returnContent.match(valuePattern);
      let type = 'unknown';
      
      if (valueMatch) {
        const value = valueMatch[1].trim();
        if (value === 'true' || value === 'false') type = 'boolean';
        else if (/^\d+$/.test(value)) type = 'number';
        else if (value.startsWith('"') || value.startsWith("'") || value.startsWith('`')) type = 'string';
        else if (value.startsWith('[')) type = 'array';
        else if (value.startsWith('{')) type = 'object';
      }
      
      variables.push({
        path: propName,
        type,
        label: formatLabel(propName),
        optional: false
      });
    }
  }
  
  return variables;
}

/**
 * Get schema for a node from backend-parsed outputSchema or fallback parsing
 * @param {string} nodeType - Node type (e.g., 'analyze_ticket')
 * @param {Object} nodeConfig - Full node config from backend (includes outputSchema)
 * @returns {Object} Schema with variables array
 */
export function getNodeSchema(nodeType, nodeConfig = null) {
  // Use backend-parsed schema if available (100% accurate)
  if (nodeConfig?.outputSchema?.variables) {
    return {
      type: 'object',
      label: `${formatLabel(nodeType)} Output`,
      variables: nodeConfig.outputSchema.variables,
      schema: nodeConfig.outputSchema.schema || { type: 'object' },
      hasCustomCode: !!nodeConfig.executeCode
    };
  }
  
  // Fallback: try to parse executeCode locally (less accurate)
  if (nodeConfig?.executeCode) {
    const customVariables = parseExecuteCodeReturns(nodeConfig.executeCode);
    if (customVariables.length > 0) {
      return {
        type: 'object',
        label: `${formatLabel(nodeType)} Output`,
        variables: customVariables,
        schema: { type: 'object' },
        hasCustomCode: true
      };
    }
  }
  
  // Last resort fallback for unknown node types
  return {
    type: 'object',
    label: `${formatLabel(nodeType)} Output`,
    variables: [
      { path: 'success', type: 'boolean', label: 'Success', optional: false }
    ],
    schema: { type: 'object', properties: { success: { type: 'boolean' } } }
  };
}

/**
 * Get grouped state variables for a node (for Variable Inspector)
 * Returns groups in execution order: Workflow Input → Node 1 → Node 2 → ...
 * @param {string} nodeId - Target node ID
 * @param {Array} nodes - All nodes in the graph
 * @param {Array} edges - All edges in the graph
 * @param {Object} stateSchema - Optional state schema from graph definition
 * @param {Object} nodeConfigs - Optional node configurations with custom executeCode
 * @returns {Array} Array of groups with variables
 */
export function getGroupedStateVariables(nodeId, nodes, edges, stateSchema = null, nodeConfigs = {}) {
  const groups = [];
  
  // Convert stateSchema to display format or use fallback
  const inputSchema = stateSchema 
    ? convertStateSchemaToDisplay(stateSchema)
    : FALLBACK_CONTEXT_SCHEMA;
  
  // Group 1: Workflow Input (from graph's stateSchema or fallback)
  groups.push({
    id: 'workflow_input',
    label: 'Workflow Input',
    stepNumber: 0,
    icon: 'input',
    variables: flattenSchemaToVariables(inputSchema, ''),
    hasSchema: !!stateSchema
  });
  
  // Get predecessor nodes in execution order
  const predecessors = getPredecessorNodesInOrder(nodeId, nodes, edges);
  
  // Add each predecessor as a group (skip decision nodes - they don't produce variables)
  predecessors.forEach((node, index) => {
    // Skip decision/conditional nodes - they only route, don't produce state
    if (node.type === 'decision' || node.data?.conditional) {
      return;
    }
    
    const nodeType = node.data?.nodeType || node.data?.type || node.type;
    const nodeLabel = node.data?.label || node.id;
    
    // Get node config from backend (includes parsed outputSchema)
    const nodeConfig = nodeConfigs[node.id];
    
    // Get schema - uses backend-parsed outputSchema for 100% accuracy
    const schema = getNodeSchema(nodeType, nodeConfig);
    
    // Node outputs are merged directly into state root
    // Show each property at the root level with a note about which node produced it
    const variables = [];

    // Add all schema variables directly at root level
    for (const v of schema.variables) {
      variables.push({
        ...v,
        path: v.path,
        fullPath: v.path,
        producedBy: nodeLabel, // Track which node produced this property
        hasCustomCode: schema.hasCustomCode
      });
    }
    
    groups.push({
      id: node.id,
      label: nodeLabel,
      nodeType,
      stepNumber: index + 1,
      icon: 'node',
      variables,
      hasCustomCode: schema.hasCustomCode
    });
  });
  
  return groups;
}

/**
 * Legacy function - get flat available state variables
 * @param {string} nodeId - Target node ID
 * @param {Array} nodes - All nodes in the graph
 * @param {Array} edges - All edges in the graph
 * @param {Object} stateSchema - Optional state schema from graph definition
 * @param {Object} nodeConfigs - Optional node configurations with custom executeCode
 * @returns {Object} Schema of available state variables
 */
export function getAvailableStateVariables(nodeId, nodes, edges, stateSchema = null, nodeConfigs = {}) {
  const groups = getGroupedStateVariables(nodeId, nodes, edges, stateSchema, nodeConfigs);
  
  // Flatten groups into single object for backward compatibility
  const inputSchema = stateSchema 
    ? convertStateSchemaToDisplay(stateSchema)
    : FALLBACK_CONTEXT_SCHEMA;
  const result = { ...inputSchema };
  
  for (const group of groups) {
    if (group.id === 'workflow_input') continue;

    const nodeType = group.nodeType;
    const nodeConfig = nodeConfigs[group.id];
    const schema = getNodeSchema(nodeType, nodeConfig);

    // Node outputs are merged directly into state root
    // Add each property from this node's output schema to the result
    for (const variable of schema.variables) {
      // If property already exists (e.g., 'success' returned by multiple nodes), keep first occurrence
      if (!result[variable.path]) {
        result[variable.path] = {
          type: variable.type,
          label: variable.label || formatLabel(variable.path),
          optional: variable.optional,
          producedBy: group.label, // Track which node first produced this
          nodeId: group.id
        };
      }
    }
  }
  
  return result;
}

/**
 * Flatten nested schema to flat variable list
 */
function flattenSchemaToVariables(schema, prefix) {
  const variables = [];
  
  for (const [key, value] of Object.entries(schema)) {
    const path = prefix ? `${prefix}.${key}` : key;
    
    variables.push({
      path,
      fullPath: path,
      type: value.type,
      label: value.label || formatLabel(key),
      optional: false
    });
    
    if (value.type === 'object' && value.properties) {
      variables.push(...flattenSchemaToVariables(value.properties, path));
    }
  }
  
  return variables;
}

/**
 * Flatten a nested schema into dot-notation paths
 */
export function flattenSchema(schema, prefix = '') {
  const results = [];
  
  for (const [key, value] of Object.entries(schema)) {
    const path = prefix ? `${prefix}.${key}` : key;
    results.push({ path, schema: value });
    
    if (value.type === 'object' && value.properties) {
      results.push(...flattenSchema(value.properties, path));
    }
  }
  
  return results;
}

/**
 * Filter variables by search query
 */
export function filterVariables(variables, query) {
  if (!query) return variables;
  
  const lowerQuery = query.toLowerCase();
  return variables.filter(v => {
    const path = v.path.toLowerCase();
    const label = (v.label || '').toLowerCase();
    return path.includes(lowerQuery) || label.includes(lowerQuery);
  });
}

/**
 * Get autocomplete suggestions for a partial variable reference
 */
export function getAutocompleteSuggestions(availableVars, partial) {
  const allPaths = flattenSchema(availableVars);
  const filtered = filterVariables(allPaths, partial);
  
  return filtered.map(({ path, schema }) => ({
    path,
    label: schema.label || path,
    type: schema.type,
    example: schema.example,
    description: schema.description
  })).slice(0, 20);
}

/**
 * Format node type to human-readable label
 */
function formatLabel(str) {
  if (!str) return '';
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
}
